import type { BookingLocale, BookingMessages } from "@/i18n/types";
import { de } from "@/i18n/de";
import { en } from "@/i18n/en";
import type { OnlineBookingMinStaySettings, OnlineBookingSeasonPeriod, PublicBookingConfig } from "@/types";
import { bookingSeasonErrorMessage, isBookingPeriodInSeasons } from "@/lib/bookingSeasons";
import { validateMinStay } from "@/lib/minStay";
import { validateSameDayCutoff } from "@/lib/sameDayCutoff";
import { validateSoldOutNights } from "@/lib/soldOut";

export type PeriodValidationMessages = Pick<
  BookingMessages["validation"],
  "selectDates" | "departureAfterArrival" | "arrivalNotPast" | "sameDayCutoffBlocked"
>;

function intlLocale(locale: BookingLocale): string {
  return locale === "en" ? "en-GB" : "de-DE";
}

export function todayLocal(): string {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

export function addDays(iso: string, days: number): string {
  const [y, m, d] = iso.split("-").map(Number);
  const dt = new Date(y, m - 1, d);
  dt.setDate(dt.getDate() + days);
  return formatIso(dt);
}

function formatIso(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

export function nightsBetween(arrival: string, departure: string): number {
  const a = new Date(arrival);
  const b = new Date(departure);
  return Math.round((b.getTime() - a.getTime()) / (1000 * 60 * 60 * 24));
}

function isFullBookingMessages(
  messages: PeriodValidationMessages | BookingMessages,
): messages is BookingMessages {
  return "validation" in messages;
}

function resolvePeriodValidation(
  messages: PeriodValidationMessages | BookingMessages,
): BookingMessages["validation"] {
  return isFullBookingMessages(messages) ? messages.validation : { ...de.validation, ...messages };
}

export function validatePeriod(
  arrival: string,
  departure: string,
  seasons: OnlineBookingSeasonPeriod[] = [],
  minStay?: OnlineBookingMinStaySettings,
  bookingConfig?: PublicBookingConfig | null,
  messages: PeriodValidationMessages | BookingMessages = de,
  locale: BookingLocale = "de",
): string | null {
  const validation = resolvePeriodValidation(messages);
  if (!arrival || !departure) return validation.selectDates;
  if (departure <= arrival) return validation.departureAfterArrival;
  if (arrival < todayLocal()) return validation.arrivalNotPast;
  const sameDayCutoffError = validateSameDayCutoff(arrival, bookingConfig, validation);
  if (sameDayCutoffError) return sameDayCutoffError;
  if (seasons.length > 0 && !isBookingPeriodInSeasons(arrival, departure, seasons)) {
    const seasonMessages = isFullBookingMessages(messages) ? messages.season : de.season;
    return bookingSeasonErrorMessage(seasons, seasonMessages, locale);
  }
  const minStayError = validateMinStay(arrival, departure, minStay, validation);
  if (minStayError) return minStayError;
  const soldOutError = validateSoldOutNights(arrival, departure, bookingConfig, validation);
  if (soldOutError) return soldOutError;
  return null;
}

/** Nur echte Validierungsfehler anzeigen, nicht den leeren Startzustand. */
export function shouldShowPeriodValidationError(
  arrival: string,
  departure: string,
  periodError: string | null,
): boolean {
  if (!periodError) return false;
  return Boolean(arrival && departure);
}

function parseLocalIsoDate(isoDate: string): Date {
  const parts = isoDate.split("-").map(Number);
  const y = parts[0] ?? 0;
  const m = parts[1] ?? 1;
  const d = parts[2] ?? 1;
  return new Date(y, m - 1, d);
}

export function formatLocaleDate(iso: string, locale: BookingLocale = "de"): string {
  return new Intl.DateTimeFormat(intlLocale(locale), {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  }).format(parseLocalIsoDate(iso));
}

export function formatIsoDateShort(isoDate: string, locale: BookingLocale = "de"): string {
  return formatLocaleDate(isoDate, locale);
}

/** @deprecated Verwende formatLocaleDate – gleiches Format (TT.MM.JJJJ). */
export function formatGermanDate(iso: string): string {
  return formatLocaleDate(iso, "de");
}

/** Lesbares Datum, z. B. „24.05.2026“. */
export function formatArrivalDayLong(isoDate: string): string {
  return formatIsoDateShort(isoDate);
}

const DATE_TIME_SHORT = new Intl.DateTimeFormat("de-DE", {
  day: "2-digit",
  month: "2-digit",
  year: "numeric",
  hour: "2-digit",
  minute: "2-digit",
});

/** Datum und Uhrzeit, z. B. „24.05.2026, 14:30“. */
export function formatDateTimeShort(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return DATE_TIME_SHORT.format(d);
}

export function formatBookingPeriodShort(
  arrival: string,
  departure: string,
  nights: number,
  locale: BookingLocale = "de",
): string {
  return formatLocaleBookingPeriodShort(arrival, departure, nights, locale);
}

export function formatLocaleBookingPeriodShort(
  arrival: string,
  departure: string,
  nights: number,
  locale: BookingLocale = "de",
  nightLabel?: string,
): string {
  const label =
    nightLabel ??
    (() => {
      const common = locale === "en" ? en.common : de.common;
      return nights === 1 ? common.night : common.nights;
    })();
  return `${formatLocaleDate(arrival, locale)} → ${formatLocaleDate(departure, locale)} (${nights} ${label})`;
}

export function clampDateToMin(isoDate: string, min: string): string {
  if (!isoDate || !min) return isoDate;
  return isoDate < min ? min : isoDate;
}

function toLocalIsoDate(year: number, month: number, day: number): string {
  const m = String(month).padStart(2, "0");
  const d = String(day).padStart(2, "0");
  return `${year}-${m}-${d}`;
}

function isIsoDateSelectable(
  iso: string,
  min?: string,
  max?: string,
  extraDisabled?: (iso: string) => boolean,
): boolean {
  if (min && iso < min) return false;
  if (max && iso > max) return false;
  if (extraDisabled?.(iso)) return false;
  return true;
}

export interface CalendarMonthView {
  year: number;
  month: number;
}

export function calendarMonthFromIso(iso: string): CalendarMonthView {
  const [y, m] = iso.split("-").map(Number);
  return { year: y ?? 0, month: m ?? 1 };
}

export function initialCalendarMonth(value: string, min?: string): CalendarMonthView {
  if (value) return calendarMonthFromIso(value);
  if (min) return calendarMonthFromIso(min);
  return calendarMonthFromIso(todayLocal());
}

export function shiftCalendarMonth(view: CalendarMonthView, delta: number): CalendarMonthView {
  const date = new Date(view.year, view.month - 1 + delta, 1);
  return { year: date.getFullYear(), month: date.getMonth() + 1 };
}

export interface CalendarDayCell {
  iso: string;
  day: number;
  inMonth: boolean;
  disabled: boolean;
}

const CALENDAR_WEEKDAYS_DE = ["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"] as const;
const CALENDAR_WEEKDAYS_EN = ["Mo", "Tu", "We", "Th", "Fr", "Sa", "Su"] as const;

export function calendarWeekdayLabels(locale: BookingLocale = "de"): readonly string[] {
  return locale === "en" ? CALENDAR_WEEKDAYS_EN : CALENDAR_WEEKDAYS_DE;
}

export function formatCalendarMonthTitle(
  view: CalendarMonthView,
  locale: BookingLocale = "de",
): string {
  return new Intl.DateTimeFormat(intlLocale(locale), {
    month: "long",
    year: "numeric",
  }).format(new Date(view.year, view.month - 1, 1));
}

export function buildCalendarMonthGrid(
  view: CalendarMonthView,
  min?: string,
  max?: string,
  extraDisabled?: (iso: string) => boolean,
): CalendarDayCell[] {
  const { year, month } = view;
  const first = new Date(year, month - 1, 1);
  const startOffset = (first.getDay() + 6) % 7;
  const gridStart = new Date(year, month - 1, 1 - startOffset);

  const cells: CalendarDayCell[] = [];
  for (let i = 0; i < 42; i++) {
    const d = new Date(gridStart.getFullYear(), gridStart.getMonth(), gridStart.getDate() + i);
    const iso = toLocalIsoDate(d.getFullYear(), d.getMonth() + 1, d.getDate());
    cells.push({
      iso,
      day: d.getDate(),
      inMonth: d.getMonth() === month - 1,
      disabled: !isIsoDateSelectable(iso, min, max, extraDisabled),
    });
  }
  return cells;
}

export function canShowPreviousCalendarMonth(view: CalendarMonthView, min?: string): boolean {
  if (!min) return true;
  const prev = shiftCalendarMonth(view, -1);
  const lastDay = new Date(prev.year, prev.month, 0).getDate();
  const lastIso = toLocalIsoDate(prev.year, prev.month, lastDay);
  return lastIso >= min;
}

/** Position of a day within the stay (arrival inclusive, departure exclusive for nights). */
export type StayRangeRole = "start" | "middle" | "end";

export function stayRangeRole(
  iso: string,
  arrival: string | undefined,
  departure: string | undefined,
): StayRangeRole | null {
  if (!arrival || !departure || departure <= arrival) return null;
  if (iso === arrival) return "start";
  if (iso === departure) return "end";
  if (iso > arrival && iso < departure) return "middle";
  return null;
}
